import React from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ModuleStep } from '@/lib/models/module';

interface ModuleProgressBarProps {
    steps: ModuleStep[];
    currentStepOrder: number;
    completedSteps: Set<number>;
    onStepClick?: (stepOrder: number) => void;
    onPrev?: () => void;
    onNext?: () => void;
    canGoPrev?: boolean;
    canGoNext?: boolean;
}

export const ModuleProgressBar: React.FC<ModuleProgressBarProps> = ({
    steps,
    currentStepOrder,
    completedSteps,
    onStepClick,
    onPrev,
    onNext,
    canGoPrev = false,
    canGoNext = false,
}) => {
    const totalSteps = steps.length;
    const completedCount = completedSteps.size;
    const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

    const sorted = [...steps].sort((a, b) => a.step_order - b.step_order);

    return (
        <div className="space-y-2">
            {/* Progress label */}
            <div className="flex items-center justify-between text-sm px-1">
                <span className="font-medium text-gray-700">
                    Steg {Math.min(currentStepOrder + 1, totalSteps)} av {totalSteps}
                </span>
                <span className="font-semibold text-[#1A4D4F]">{progressPercent}%</span>
            </div>

            {/* Step dots with nav buttons */}
            <div className="flex items-center gap-3">
                {/* Previous button */}
                <button
                    onClick={onPrev}
                    disabled={!canGoPrev}
                    className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${
                        canGoPrev
                            ? 'text-gray-700 bg-gray-100 hover:bg-gray-200 hover:text-gray-900'
                            : 'text-gray-300 bg-gray-50 cursor-not-allowed'
                    }`}
                    aria-label="Föregående steg"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>

                {/* Step dots */}
                <div className="flex items-center flex-1 gap-0">
                    {sorted.map((step, idx) => {
                        const isCompleted = completedSteps.has(step.step_order);
                        const isCurrent = step.step_order === currentStepOrder;
                        const isLast = idx === sorted.length - 1;
                        const canNavigate = isCompleted || isCurrent;

                        return (
                            <React.Fragment key={step.id}>
                                <button
                                    onClick={() => canNavigate && onStepClick?.(step.step_order)}
                                    disabled={!canNavigate}
                                    className={`
                                        w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all flex-shrink-0
                                        ${canNavigate ? 'cursor-pointer' : 'cursor-not-allowed'}
                                        ${isCurrent
                                            ? 'bg-[#A4C3B2] text-[#1A4D4F] ring-2 ring-[#1A4D4F] ring-offset-1'
                                            : isCompleted
                                                ? 'bg-[#1A4D4F] text-[#f5f5f5] hover:bg-[#1A4D4F]/90'
                                                : 'bg-gray-200 text-gray-400'
                                        }
                                    `}
                                    title={step.step_title}
                                >
                                    {isCompleted && !isCurrent ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                                </button>
                                {!isLast && (
                                    <div
                                        className={`flex-1 h-0.5 mx-1 min-w-3 ${
                                            isCompleted ? 'bg-[#1A4D4F]' : 'bg-gray-200'
                                        }`}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Next button */}
                <button
                    onClick={onNext}
                    disabled={!canGoNext}
                    className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${
                        canGoNext
                            ? 'text-gray-700 bg-gray-100 hover:bg-gray-200 hover:text-gray-900'
                            : 'text-gray-300 bg-gray-50 cursor-not-allowed'
                    }`}
                    aria-label="Nästa steg"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
};
